import type { IUser } from '@pulse/shared';
import React from 'react';

import { useCurrentUser } from '../features/auth/hooks/useCurrentUser';
import { HAS_AUTHENTICATED_KEY } from '../lib/api-client';

interface AuthContextValue {
  user: IUser | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isLoading, isError } = useCurrentUser();

  // Marks this browser as having had a real session at least once — api-client.ts's
  // forceLogout() reads this to tell a genuinely expired session apart from a visitor who was
  // never logged in here at all, both of which otherwise look identical (a 401 on /auth/me
  // followed by a failed refresh).
  React.useEffect(() => {
    if (user) {
      localStorage.setItem(HAS_AUTHENTICATED_KEY, '1');
    }
  }, [user]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !isLoading && !isError && user !== undefined,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
